import { useState } from "react";
import axios from "axios";
import { Field, inputCls } from "@/components/FormControls";
import { usePageTitle } from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "oms_admin_token";

const Detail = ({ label, value }) =>
  value ? (
    <p className="text-base mt-1">
      <span className="font-semibold text-primary">{label}: </span>
      <span className="text-foreground">{Array.isArray(value) ? value.join(", ") : value}</span>
    </p>
  ) : null;

const AdvisorCard = ({ a }) => (
  <div className="bg-card border border-border rounded-lg p-6" data-testid={`admin-advisor-${a.id}`}>
    <p className="text-xl font-semibold text-primary">{a.name}</p>
    <Detail label="Submitted" value={a.created_at ? new Date(a.created_at).toLocaleString() : ""} />
    <Detail label="Email" value={a.email} />
    <Detail label="Phone" value={a.phone} />
    <Detail label="City" value={a.city} />
    <Detail label="Experience area" value={a.experience_area} />
    <Detail label="Years of experience" value={a.years_experience} />
    <Detail label="Career/life learning" value={a.learning} />
    <Detail label="Especially good at" value={a.strengths} />
    <Detail label="People asked for help with" value={a.help_requests} />
    <Detail label="Could teach in an afternoon" value={a.teach_afternoon} />
    <Detail label="Interested in" value={a.interests} />
    <Detail label="Availability" value={a.availability} />
  </div>
);

const RequestCard = ({ r }) => (
  <div className="bg-card border border-border rounded-lg p-6" data-testid={`admin-request-${r.id}`}>
    <p className="text-xl font-semibold text-primary">{r.name}</p>
    <Detail label="Submitted" value={r.created_at ? new Date(r.created_at).toLocaleString() : ""} />
    <Detail label="Company/organization" value={r.company} />
    <Detail label="Email" value={r.email} />
    <Detail label="Phone" value={r.phone} />
    <Detail label="City" value={r.city} />
    <Detail label="Help for" value={r.help_for} />
    <Detail label="Problem" value={r.problem} />
    <Detail label="Experience that would help" value={r.experience_kind} />
    <Detail label="Timing" value={r.timing} />
  </div>
);

export default function Admin() {
  usePageTitle("Admin | One More Shift", "One More Shift admin submissions dashboard.");
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("advisors");
  const [loading, setLoading] = useState(false);

  const load = async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setData(res.data);
    } catch (e) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setError("Session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      load(res.data.token);
    } catch (err) {
      setError("Invalid email or password.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login-page">
        <h1 className="text-3xl md:text-4xl font-bold">Admin Sign In</h1>
        <form onSubmit={login} className="mt-8 space-y-6" data-testid="admin-login-form">
          <Field label="Email" id="admin-email" required>
            <input id="admin-email" type="email" data-testid="admin-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Password" id="admin-password" required>
            <input id="admin-password" type="password" data-testid="admin-password-input" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          {error && (
            <p className="text-destructive text-lg font-semibold" role="alert" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button type="submit" data-testid="admin-login-button" className="w-full bg-primary text-primary-foreground text-xl font-semibold px-8 py-4 rounded-md transition-colors duration-200 hover:bg-[#15325a]">
            Sign In
          </button>
        </form>
      </div>
    );
  }

  if (!data) {
    if (!loading) load(token);
    return (
      <div className="max-w-5xl mx-auto px-6 py-24 text-center text-lg" data-testid="admin-loading">
        Loading submissions...
      </div>
    );
  }

  const advisors = data.advisor_applications || [];
  const requests = data.experience_requests || [];

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-bold">Submissions</h1>
        <button onClick={logout} data-testid="admin-logout-button" className="border-2 border-primary text-primary text-lg font-semibold px-6 py-2 rounded-md hover:bg-primary hover:text-primary-foreground transition-colors duration-200">
          Sign Out
        </button>
      </div>

      <div className="mt-8 flex gap-3 flex-wrap">
        <button
          data-testid="admin-tab-advisors"
          onClick={() => setTab("advisors")}
          className={`text-lg font-semibold px-6 py-3 rounded-md border transition-colors duration-200 ${tab === "advisors" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
        >
          Advisor Applications ({advisors.length})
        </button>
        <button
          data-testid="admin-tab-requests"
          onClick={() => setTab("requests")}
          className={`text-lg font-semibold px-6 py-3 rounded-md border transition-colors duration-200 ${tab === "requests" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
        >
          Experience Requests ({requests.length})
        </button>
      </div>

      <div className="mt-8 space-y-6">
        {tab === "advisors" &&
          (advisors.length ? (
            advisors.map((a) => <AdvisorCard key={a.id} a={a} />)
          ) : (
            <p className="text-lg text-muted-foreground" data-testid="admin-no-advisors">No advisor applications yet.</p>
          ))}
        {tab === "requests" &&
          (requests.length ? (
            requests.map((r) => <RequestCard key={r.id} r={r} />)
          ) : (
            <p className="text-lg text-muted-foreground" data-testid="admin-no-requests">No experience requests yet.</p>
          ))}
      </div>
    </div>
  );
}
